// The staff-facing vocabulary for permissions.
//
// admin-svc's /v1/admin/permissions returns raw slugs ("payouts.kyc_verify")
// plus a category derived from the segment before the dot. Those slugs are an
// engineering identifier, not something an ops lead should have to decode when
// they're deciding who can refund a rider. This module is the translation
// layer: every permission gets a label, a plain description of what the holder
// can actually DO, a risk level, and an area that matches how the business is
// organised rather than how the API namespaces are.
//
// FORWARD-COMPATIBLE BY CONSTRUCTION: the catalogue is still driven by the API
// response — this file only decorates it. A permission added server-side that
// nobody has described here still renders (with a label derived from its slug)
// in an "Other" area, so a new backend capability can never silently vanish
// from the role builder. Keep this file in step with
// services/admin/internal/rbac/rbac.go when you add one.

/** How much damage a permission can do — drives the marker in the picker and
 *  the summary shown before a role is assigned to someone. */
export type PermissionRisk =
  /** Read-only. Sees data, changes nothing. */
  | 'view'
  /** Writes business data (edits, approvals, workflow moves). */
  | 'change'
  /** Moves money, suspends accounts, messages everyone, or grants access.
   *  These are the ones worth a second look during an access review. */
  | 'critical';

export interface PermissionMeta {
  /** Staff-facing name, sentence case. */
  label: string;
  /** One line on what the holder can do. Shown under the label. */
  description: string;
  /** Area key — see AREAS below. */
  area: string;
  risk: PermissionRisk;
}

/** Areas, in the order they appear in the builder. The order is deliberate: it
 *  walks the business from who you serve, through the work, to the back office. */
export const AREAS: Array<{ key: string; label: string; blurb: string }> = [
  { key: 'overview', label: 'Overview', blurb: 'Landing dashboard and platform totals.' },
  { key: 'riders', label: 'Riders', blurb: 'Rider accounts, profiles and account standing.' },
  { key: 'drivers', label: 'Drivers', blurb: 'Driver accounts, documents and service lines.' },
  { key: 'trips', label: 'Trips', blurb: 'Live and historic trips, reserves and status labels.' },
  { key: 'money', label: 'Money', blurb: 'Wallets, payments, payouts, expenses and fares.' },
  { key: 'care', label: 'Support and safety', blurb: 'Support inbox, complaints and SOS incidents.' },
  { key: 'growth', label: 'Growth', blurb: 'Promo codes, driver campaigns and boosts.' },
  { key: 'comms', label: 'Content and messaging', blurb: 'In-app content and push notifications.' },
  { key: 'config', label: 'Configuration', blurb: 'Zones, products, pricing classes and feature switches.' },
  { key: 'boats', label: 'Boats', blurb: 'Docks and the dock distance matrix.' },
  { key: 'admin', label: 'Administration', blurb: 'Audit trail and staff access.' },
  { key: 'other', label: 'Other', blurb: 'Newer permissions not yet categorised.' },
];

/** slug → meta. Anything missing falls through to deriveMeta(). */
const CATALOG: Record<string, PermissionMeta> = {
  // ── Overview ──
  'dashboard.read': {
    label: 'View dashboard',
    description: 'Open the landing dashboard and its headline totals.',
    area: 'overview',
    risk: 'view',
  },

  // ── Riders ──
  'customers.read': {
    label: 'View riders',
    description: 'Search riders, open a profile and read their trip history.',
    area: 'riders',
    risk: 'view',
  },
  'customers.manage': {
    label: 'Edit rider details',
    description: "Correct a rider's name or email address.",
    area: 'riders',
    risk: 'change',
  },
  'customers.block': {
    label: 'Block riders',
    description: 'Suspend or restore a rider account. Takes effect immediately.',
    area: 'riders',
    risk: 'critical',
  },

  // ── Drivers ──
  'drivers.read': {
    label: 'View drivers',
    description: 'Search drivers, open a profile and read their documents.',
    area: 'drivers',
    risk: 'view',
  },
  'documents.review': {
    label: 'Review driver documents',
    description: 'Approve or reject licences, insurance and vehicle paperwork.',
    area: 'drivers',
    risk: 'change',
  },
  'drivers.service_line': {
    label: 'Set service lines',
    description: 'Choose which products a driver is allowed to accept.',
    area: 'drivers',
    risk: 'change',
  },
  'drivers.ban': {
    label: 'Block drivers',
    description: 'Suspend or restore a driver account. Ends their shift immediately.',
    area: 'drivers',
    risk: 'critical',
  },

  // ── Trips ──
  'trips.read': {
    label: 'View trips',
    description: 'Browse live and completed trips, and open trip detail.',
    area: 'trips',
    risk: 'view',
  },
  'reserves.assign': {
    label: 'Assign reserves',
    description: 'Offer a scheduled booking to a specific driver.',
    area: 'trips',
    risk: 'change',
  },
  'trips.cancel': {
    label: 'Cancel trips',
    description: 'Force-cancel a trip that is already under way.',
    area: 'trips',
    risk: 'critical',
  },
  'booking_statuses.read': {
    label: 'View booking statuses',
    description: 'See the status labels used across both apps.',
    area: 'trips',
    risk: 'view',
  },
  'booking_statuses.manage': {
    label: 'Edit booking statuses',
    description: 'Rename the status labels riders and drivers see.',
    area: 'trips',
    risk: 'change',
  },

  // ── Money ──
  'wallet.read': {
    label: 'View wallets',
    description: 'Read rider and driver balances and ledger history.',
    area: 'money',
    risk: 'view',
  },
  'payments.read': {
    label: 'View payments',
    description: 'Read card payment history and its outcomes.',
    area: 'money',
    risk: 'view',
  },
  'payouts.read': {
    label: 'View payouts',
    description: 'Read driver payout history and its status.',
    area: 'money',
    risk: 'view',
  },
  'driver_tips.read': {
    label: 'View tips',
    description: 'Read the record of tips riders left for drivers.',
    area: 'money',
    risk: 'view',
  },
  'expenses.read': {
    label: 'View expense claims',
    description: 'Read driver work-expense submissions.',
    area: 'money',
    risk: 'view',
  },
  'fares.read': {
    label: 'View fares',
    description: 'Read the rate card for each product.',
    area: 'money',
    risk: 'view',
  },
  'expenses.manage': {
    label: 'Decide expense claims',
    description: 'Approve or reject a driver reimbursement.',
    area: 'money',
    risk: 'critical',
  },
  'wallet.adjust': {
    label: 'Adjust balances',
    description: 'Credit or debit a wallet by hand. Real money, no approval step.',
    area: 'money',
    risk: 'critical',
  },
  'payouts.kyc_verify': {
    label: 'Verify payout bank details',
    description: "Confirm a driver's bank record so payouts can run.",
    area: 'money',
    risk: 'critical',
  },
  'fares.manage': {
    label: 'Edit fares',
    description: 'Change rates per product. Applies to every new quote.',
    area: 'money',
    risk: 'critical',
  },
  'fares.edit': {
    label: 'Edit fares (legacy)',
    description: 'Superseded by Edit fares. Kept so older roles keep working.',
    area: 'money',
    risk: 'change',
  },

  // ── Support and safety ──
  'support.read': {
    label: 'View support inbox',
    description: 'Read rider conversations with the support team.',
    area: 'care',
    risk: 'view',
  },
  'support.manage': {
    label: 'Handle support threads',
    description: 'Claim a thread, reply to the rider and close it out.',
    area: 'care',
    risk: 'change',
  },
  'complaints.read': {
    label: 'View complaints',
    description: 'Read complaints raised against trips, riders or drivers.',
    area: 'care',
    risk: 'view',
  },
  'complaints.manage': {
    label: 'Progress complaints',
    description: 'Move a complaint through its workflow to resolution.',
    area: 'care',
    risk: 'change',
  },
  'safety.read': {
    label: 'View safety incidents',
    description: 'Read the SOS and safety triage queue.',
    area: 'care',
    risk: 'view',
  },
  'safety.manage': {
    label: 'Resolve safety incidents',
    description: 'Acknowledge and close an SOS report.',
    area: 'care',
    risk: 'critical',
  },

  // ── Growth ──
  'promos.read': {
    label: 'View promo codes',
    description: 'Read rider promo codes and how they are performing.',
    area: 'growth',
    risk: 'view',
  },
  'promos.manage': {
    label: 'Manage promo codes',
    description: 'Create, edit and retire rider promo codes.',
    area: 'growth',
    risk: 'change',
  },
  'incentives.read': {
    label: 'View driver campaigns',
    description: 'Read quests, streaks and earnings guarantees.',
    area: 'growth',
    risk: 'view',
  },
  'incentives.manage': {
    label: 'Run driver campaigns',
    description: 'Create and edit quests, streaks and earnings guarantees.',
    area: 'growth',
    risk: 'change',
  },
  'boosts.read': {
    label: 'View boosts',
    description: 'Read the driver earnings boosts in effect.',
    area: 'growth',
    risk: 'view',
  },

  // ── Content and messaging ──
  'cms.read': {
    label: 'View content',
    description: 'Read FAQs, policies and in-app copy.',
    area: 'comms',
    risk: 'view',
  },
  'cms.manage': {
    label: 'Edit content',
    description: 'Publish FAQs, policies and in-app copy. Goes live on save.',
    area: 'comms',
    risk: 'change',
  },
  'notifications.read': {
    label: 'View push history',
    description: 'Read what has been broadcast, by whom and when.',
    area: 'comms',
    risk: 'view',
  },
  'notifications.send': {
    label: 'Send push notifications',
    description: 'Broadcast to riders or drivers. Cannot be recalled.',
    area: 'comms',
    risk: 'critical',
  },

  // ── Boats ──
  'docks.read': {
    label: 'View docks',
    description: 'Read the Boats docks and the distance matrix.',
    area: 'boats',
    risk: 'view',
  },
  'docks.manage': {
    label: 'Edit docks',
    description: 'Add docks and set the distances used to price Boats trips.',
    area: 'boats',
    risk: 'change',
  },

  // ── Configuration (split out of cms.* on 2026-07-20) ──
  'zones.read': {
    label: 'View zones',
    description: 'Read the operating zones and their boundaries.',
    area: 'config',
    risk: 'view',
  },
  'zones.manage': {
    label: 'Edit zones',
    description: 'Draw and retire operating zones. Changes where riders can book.',
    area: 'config',
    risk: 'critical',
  },
  'trip_types.read': {
    label: 'View trip types',
    description: 'Read the product list riders choose from.',
    area: 'config',
    risk: 'view',
  },
  'trip_types.manage': {
    label: 'Edit trip types',
    description: 'Add products or switch them off. Hides them in the rider app.',
    area: 'config',
    risk: 'critical',
  },
  'vehicle_categories.read': {
    label: 'View vehicle categories',
    description: 'Read the vehicle classes and what each one is priced at.',
    area: 'config',
    risk: 'view',
  },
  'chargers.read': {
    label: 'View chargers',
    description: 'Read the charging locations shown to drivers.',
    area: 'config',
    risk: 'view',
  },
  'chargers.manage': {
    label: 'Edit chargers',
    description: 'Add and remove charging locations.',
    area: 'config',
    risk: 'change',
  },
  'membership.read': {
    label: 'View membership tiers',
    description: 'Read the Telride One tiers and their benefits.',
    area: 'config',
    risk: 'view',
  },
  'membership.manage': {
    label: 'Edit membership tiers',
    description: 'Change tier pricing and benefits. Affects existing members.',
    area: 'config',
    risk: 'critical',
  },
  'features.read': {
    label: 'View Operations settings',
    description: 'Read which product features are switched on (the Operations page).',
    area: 'config',
    risk: 'view',
  },
  'features.manage': {
    label: 'Change Operations settings',
    description: 'Switch product features on and off across the live apps.',
    area: 'config',
    risk: 'critical',
  },
  'destinations.read': {
    label: 'View Going Home settings',
    description: "Read the rules for the driver's destination filter.",
    area: 'config',
    risk: 'view',
  },
  'destinations.manage': {
    label: 'Edit Going Home settings',
    description: 'Change how often drivers can use the destination filter.',
    area: 'config',
    risk: 'change',
  },
  'complaint_categories.read': {
    label: 'View complaint categories',
    description: 'Read the categories riders pick when raising a complaint.',
    area: 'config',
    risk: 'view',
  },
  'complaint_categories.manage': {
    label: 'Edit complaint categories',
    description: 'Add or rename the complaint categories shown in the apps.',
    area: 'config',
    risk: 'change',
  },

  // ── Growth (quests split out of incentives.*) ──
  'quests.read': {
    label: 'View quests',
    description: 'Read driver quests, their tiers and who opted in.',
    area: 'growth',
    risk: 'view',
  },
  'quests.manage': {
    label: 'Manage quests',
    description: 'Create and edit driver quests and their reward tiers.',
    area: 'growth',
    risk: 'change',
  },

  // ── Split out of drivers.read / payouts.read ──
  'verification_log.read': {
    label: 'View ID verification log',
    description: 'Read the record of driver identity checks.',
    area: 'drivers',
    risk: 'view',
  },
  'driver_earnings.read': {
    label: 'View driver earnings',
    description: 'Read per-driver earnings summaries.',
    area: 'money',
    risk: 'view',
  },

  // ── One permission per remaining sidebar item ──
  'reserve_trips.read': {
    label: 'View reserve trips',
    description: 'Read scheduled bookings and who they are assigned to.',
    area: 'trips',
    risk: 'view',
  },
  'boat_trips.read': {
    label: 'View boat bookings',
    description: 'Read Telride Boats dock-to-dock charters.',
    area: 'boats',
    risk: 'view',
  },
  'customer_identity.read': {
    label: 'View customer identity checks',
    description: 'Read rider identity verification records.',
    area: 'riders',
    risk: 'view',
  },
  'streaks.read': {
    label: 'View streaks',
    description: 'Read driver streak progress.',
    area: 'growth',
    risk: 'view',
  },
  'guarantees.read': {
    label: 'View guarantee enrolments',
    description: 'Read which drivers joined an earnings guarantee.',
    area: 'growth',
    risk: 'view',
  },
  'speeding.read': {
    label: 'View speeding reports',
    description: 'Read the speeding events flagged on trips.',
    area: 'care',
    risk: 'view',
  },
  'push_log.read': {
    label: 'View push log',
    description: 'Read the delivery record of sent notifications.',
    area: 'comms',
    risk: 'view',
  },
  'bank_kyc_review.read': {
    label: 'View bank-KYC review queue',
    description: 'Read driver bank records awaiting verification.',
    area: 'money',
    risk: 'view',
  },

  // ── Administration ──
  'otp_codes.read': {
    label: 'Read sign-in codes',
    description: 'See live OTP codes for any account. Enough to sign in as them.',
    area: 'admin',
    risk: 'critical',
  },
  'audit.read': {
    label: 'View audit log',
    description: 'Read the record of every staff action taken in this portal.',
    area: 'admin',
    risk: 'view',
  },
  'staff.manage': {
    label: 'Manage staff and roles',
    description: 'Add staff, change roles, reset passwords and edit permissions.',
    area: 'admin',
    risk: 'critical',
  },
};

/** The four built-in roles ship with an EMPTY description from admin-svc (they
 *  are defined as a permission matrix in code, with no prose attached), so the
 *  list used to render a bare dash next to every one of them. These are the
 *  staff-facing summaries — what the role is FOR, in one line. Custom roles use
 *  whatever description their author wrote. */
const SYSTEM_ROLE_BLURBS: Record<string, string> = {
  superadmin: 'Unrestricted. Every screen and every action, including staff access.',
  ops: 'Day-to-day operations: riders, drivers, trips, support and safety. No money.',
  finance: 'Payments, payouts, expenses and fares. No operational controls.',
  support: 'The support inbox and complaints, with read access to riders and trips.',
};

/** Description to show for a role — the author's text, or the built-in summary
 *  for a system role that has none. */
export function roleBlurb(name: string, description: string, isSystem: boolean): string {
  const text = description.trim();
  if (text) return text;
  if (isSystem) return SYSTEM_ROLE_BLURBS[name] ?? '';
  return '';
}

/** Title-cases a slug into something readable when we have no entry for it —
 *  "quests.manage" → "Manage quests". Better than showing the raw slug. */
function deriveMeta(permission: string): PermissionMeta {
  const [subject = permission, action = ''] = permission.split('.');
  const words = subject.replace(/_/g, ' ');
  const verbs: Record<string, string> = {
    read: 'View',
    manage: 'Manage',
    send: 'Send',
    edit: 'Edit',
    review: 'Review',
    cancel: 'Cancel',
    assign: 'Assign',
    adjust: 'Adjust',
    block: 'Block',
    ban: 'Block',
  };
  const verb = verbs[action] ?? 'Access';
  return {
    label: `${verb} ${words}`,
    description: 'Added recently. Ask engineering what this covers before granting it.',
    area: 'other',
    // Unknown writes are treated as risky until someone describes them.
    risk: action === 'read' ? 'view' : 'change',
  };
}

/** Look up a permission's staff-facing metadata. Never returns undefined. */
export function permissionMeta(permission: string): PermissionMeta {
  return CATALOG[permission] ?? deriveMeta(permission);
}

/** True when this permission can move money, suspend an account, message the
 *  whole user base, or hand out access. Used for the review markers. */
export function isCritical(permission: string): boolean {
  return permissionMeta(permission).risk === 'critical';
}

/** Human summary of a permission set, e.g. "Full access" or
 *  "18 permissions across Riders, Trips and Money". Used in the roles list and
 *  before assigning a role to a staff member. */
export function describeAccess(permissions: string[], catalogSize: number): string {
  if (permissions.length === 0) return 'No access';
  if (catalogSize > 0 && permissions.length >= catalogSize) return 'Full access';

  const areaLabels = new Map<string, string>(AREAS.map((a) => [a.key, a.label]));
  const areas: string[] = [];
  for (const p of permissions) {
    const label = areaLabels.get(permissionMeta(p).area);
    if (label && !areas.includes(label)) areas.push(label);
  }
  const count = `${permissions.length} permission${permissions.length === 1 ? '' : 's'}`;
  if (areas.length === 0) return count;
  if (areas.length <= 3) return `${count} across ${listSentence(areas)}`;
  return `${count} across ${areas.length} areas`;
}

/** ["a","b","c"] → "a, b and c" — Australian English, no Oxford comma. */
export function listSentence(items: string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/** Groups a permission list into display areas, preserving AREAS order and
 *  dropping areas with nothing in them. Within an area, views sort before
 *  changes so the picker reads from least to most powerful. */
export function groupByArea<T extends { permission: string }>(
  items: T[],
): Array<{ key: string; label: string; blurb: string; items: T[] }> {
  const byArea = new Map<string, T[]>();
  for (const item of items) {
    const { area } = permissionMeta(item.permission);
    const bucket = byArea.get(area) ?? [];
    bucket.push(item);
    byArea.set(area, bucket);
  }
  const riskOrder: Record<PermissionRisk, number> = { view: 0, change: 1, critical: 2 };
  return AREAS.filter((a) => (byArea.get(a.key)?.length ?? 0) > 0).map((a) => ({
    ...a,
    items: (byArea.get(a.key) ?? []).sort((x, y) => {
      const mx = permissionMeta(x.permission);
      const my = permissionMeta(y.permission);
      return riskOrder[mx.risk] - riskOrder[my.risk] || mx.label.localeCompare(my.label);
    }),
  }));
}
